import { US_COUNTRY_ID } from "@daytrip/legacy-config";
import { Currency, DistanceUnit, DynamicFormConfigEntityType, KYCLevel, PayoutType } from "@daytrip/legacy-enums";
import { isUndefinedOrNull, transformPhoneNumberToNormalizedForm } from "@daytrip/utils";
import { Expose, plainToClass, Transform, Type } from "class-transformer";
import {
    IsArray,
    IsBoolean,
    IsDate,
    IsDefined,
    IsEmail,
    IsEnum,
    IsInt,
    IsNotEmpty,
    IsNumber,
    IsOptional,
    IsString,
    IsUrl,
    IsUUID,
    Length,
    Validate,
    ValidateIf,
    ValidateNested,
} from "class-validator";
import { v4 as uuid } from "uuid";

import { GAuthorized, GField, GObjectType } from "../graphql";
import { CDBaseEntity, CDColumn, CDEntity, CDIndex, CDObjectIdColumn } from "../orm";
import { TransformToDate } from "../transformers/transformToDate";
import type { CompanyDriverUser, DriversCompanyUser, DriverUser } from "../types/UserTypes";

import { BillingInformation } from "./BillingInformation";
import { Driver } from "./Driver";
import { DriversCompany } from "./DriversCompany";
import { DynamicFormFieldsValues } from "./DynamicFormFieldsValues";
import { HyperwalletInformation } from "./HyperwalletInformation";
import { MangopayInformation } from "./MangopayInformation";
import { ProfilePhoto } from "./ProfilePhoto";
import { StripeInformation } from "./StripeInformation";
import { SuspiciousCCActivityIssue } from "./SuspiciousCCActivityIssue";
import { TravelAgent } from "./TravelAgent";
import { UserAutomaticEmail } from "./UserAutomaticEmail";
import { UserRoles } from "./UserRoles";
import { Wishlist } from "./Wishlist";

@CDEntity("users")
@GObjectType("User")
export class User extends CDBaseEntity {
    @GField()
    @CDObjectIdColumn()
    @IsUUID("4", { always: true })
    @Expose()
    public _id: string = uuid();

    @CDColumn()
    @IsDate({ always: true })
    @IsDefined({ always: true })
    @Expose()
    @TransformToDate
    public createdAt: Date = new Date();

    @CDColumn()
    @IsOptional({ always: true })
    @IsDate({ always: true })
    @Expose()
    @TransformToDate
    public deletedAt?: Date;

    @GAuthorized()
    @GField({ nullable: true })
    @CDColumn()
    @IsOptional({ always: true })
    @IsDate({ groups: ["profile"] })
    @Expose()
    @TransformToDate
    public birthdayAt?: Date;

    @GField({ nullable: true })
    @CDColumn()
    @IsUUID("4", { groups: ["profile"] })
    @IsOptional({ always: true })
    @Expose()
    public countryId?: string;

    @GAuthorized()
    @GField({ nullable: true })
    @CDColumn()
    @IsString()
    @Expose()
    @CDIndex()
    public facebookId?: string;

    @GAuthorized()
    @CDColumn()
    @IsString()
    @Expose()
    @CDIndex()
    public appleUserId?: string;

    @GAuthorized()
    @CDColumn()
    @IsString()
    @Expose()
    public appleUserToken?: string;

    @CDColumn()
    @Expose()
    @Type(() => ProfilePhoto)
    @ValidateNested({ groups: ["profile"] })
    @IsOptional({ always: true })
    public profilePhoto?: ProfilePhoto;

    @GField({ nullable: true })
    @CDColumn()
    @Expose()
    @IsOptional({ always: true })
    @IsUrl(undefined, { groups: ["profile"] })
    public profilePhotoUrl?: string;

    @GAuthorized()
    @GField()
    @CDColumn()
    @IsDefined({ groups: ["profile", "credentials"] })
    @IsEmail(undefined, { groups: ["profile", "credentials"] })
    @Length(5, 48, { groups: ["profile", "credentials"] })
    @IsString({ groups: ["profile", "credentials"] })
    @ValidateIf(
        (user) =>
            !user.driver ||
            (user.driver && !user.driver.driversCompanyUserId) ||
            (user.driver && user.driver.driversCompanyUserId && user.email && user.email.length > 0),
        {
            groups: ["profile", "credentials"],
        },
    )
    @Expose()
    @Transform((value) => value && (value.trim() as string).toLowerCase())
    public email?: string = "";

    @CDColumn()
    @IsArray()
    @IsDefined()
    @Expose()
    @Transform((value) => (value && Array.isArray(value) ? value.filter((item) => String(item) !== "") : []))
    @Type(() => String)
    public otherEmails: string[] = [];

    @CDColumn()
    @IsEmail()
    @Length(5, 48)
    @IsString()
    @Expose()
    @Transform((value) => (!value ? undefined : value))
    public paypalEmail?: string = "";

    @CDColumn()
    @IsString({ groups: ["credentials"] })
    @IsDefined({ groups: ["credentials"] })
    @Expose()
    public passwordHash: string = `purposefullyInvalidInitialHash${Math.floor(Math.random() * 1000000000)}`;

    @GField()
    @CDColumn()
    @Length(0, 48, { groups: ["profile"] })
    @IsString({ groups: ["profile"] })
    @IsNotEmpty({ groups: ["profile"] })
    @ValidateIf((user: User) => !!user.driver || !!user.driversCompany || !!user.travelAgent, { groups: ["profile"] })
    @Expose()
    public firstName: string = "";

    @GField()
    @CDColumn()
    @Length(0, 48, { groups: ["profile"] })
    @IsString({ groups: ["profile"] })
    @IsNotEmpty({ groups: ["profile"] })
    @ValidateIf((user: User) => !!user.driver || !!user.driversCompany || !!user.travelAgent, { groups: ["profile"] })
    @Expose()
    public lastName: string = "";

    /**
     * The transformer checks the `countryId` of the instance. If the country is not the US,
     * it unsets the `regionId` of the nested `driver`.
     */
    @CDColumn()
    @ValidateNested({ groups: ["profile", "driver"] })
    @ValidateIf((_user, driver) => !isUndefinedOrNull(driver), { groups: ["profile", "driver"] })
    @Expose()
    @Type(() => Driver)
    @Transform((value, obj) => {
        if (value instanceof Driver && obj.countryId !== US_COUNTRY_ID) {
            value.regionId = undefined;
        }

        return value;
    })
    public driver?: Driver;

    @CDColumn()
    @ValidateNested({ groups: ["profile", "driverCompany"] })
    @ValidateIf((_user, driversCompany) => !isUndefinedOrNull(driversCompany), { groups: ["profile", "driverCompany"] })
    @Expose()
    @Type(() => DriversCompany)
    public driversCompany?: DriversCompany;

    @GAuthorized()
    @GField(() => TravelAgent)
    @CDColumn()
    @ValidateNested({ groups: ["travelAgent"] })
    @ValidateIf((_user, travelAgent) => !isUndefinedOrNull(travelAgent), { groups: ["travelAgent"] })
    @Expose()
    @Type(() => TravelAgent)
    public travelAgent?: TravelAgent;

    @GAuthorized()
    @GField()
    @CDColumn()
    @Length(4, 18, { groups: ["profile"] })
    @ValidateIf((user) => !!user.driver, { groups: ["profile"] })
    @IsString({ groups: ["profile"] })
    @IsDefined({ groups: ["profile"] })
    @Transform((value) => transformPhoneNumberToNormalizedForm(value))
    @Expose()
    public phoneNumber: string = "";

    @CDColumn()
    @Length(0, 18, { groups: ["profile"] })
    @IsString({ groups: ["profile"] })
    @IsDefined({ groups: ["profile"] })
    @Transform((value) => transformPhoneNumberToNormalizedForm(value))
    @Expose()
    public whatsappNumber: string = "";

    @CDColumn()
    @Expose()
    @IsEnum(PayoutType, { groups: ["billing", "driver", "driverCompany"], message: "invalid payout type" })
    @IsOptional({ groups: ["billing", "driver", "driverCompany"] })
    public payoutType?: PayoutType;

    @GAuthorized()
    @GField(() => [MangopayInformation])
    @CDColumn()
    @ValidateNested()
    @Transform((value: MangopayInformation[]) => (!value ? [] : plainToClass(MangopayInformation, value)))
    @Expose()
    @Type(() => MangopayInformation)
    public mangopayInformation: MangopayInformation[] = [];

    @CDColumn()
    @ValidateNested({ groups: ["hyperwalletInformation"] })
    @ValidateIf((_user, hyperwalletInformation) => !!hyperwalletInformation, { groups: ["hyperwalletInformation"] })
    @Expose()
    @Type(() => HyperwalletInformation)
    public hyperwalletInformation: HyperwalletInformation;

    @CDColumn()
    @ValidateNested({ groups: ["stripeInformation"] })
    @ValidateIf((_user, stripeInformation) => !!stripeInformation, { groups: ["stripeInformation"] })
    @Expose()
    @Type(() => StripeInformation)
    public stripeInformation: StripeInformation;

    @CDColumn()
    @ValidateNested({ groups: ["billing"] })
    @Validate(BillingInformation, { groups: ["billing"] })
    @Transform((value: BillingInformation[]) => (!value ? [] : plainToClass(BillingInformation, value)))
    @Expose()
    @Type(() => BillingInformation)
    public billingInformation: BillingInformation[] = [];

    @CDColumn()
    @ValidateNested({ groups: ["profile", "driver", "driverCompany"] })
    @Validate(DynamicFormFieldsValues, { groups: ["profile", "driver", "driverCompany"] })
    @Expose()
    @Type(() => DynamicFormFieldsValues)
    public dynamicFormFieldsValues: DynamicFormFieldsValues;

    @CDColumn()
    @IsArray({ groups: ["profile"] })
    @IsDefined({ groups: ["profile"] })
    @Type(() => String)
    @Transform((value) =>
        value && Array.isArray(value)
            ? value.map(transformPhoneNumberToNormalizedForm).filter((item) => String(item) !== "")
            : [],
    )
    @Expose()
    public otherPhoneNumbers: string[] = [];

    @CDColumn()
    @Expose()
    @Type(() => UserRoles)
    @ValidateNested({ groups: ["profile"] })
    @IsOptional({ always: true })
    public userRoles?: UserRoles;

    @CDColumn()
    @IsArray()
    @IsDefined()
    @Expose()
    @Type(() => String)
    public vehicleIds: string[] = [];

    @CDColumn()
    @IsArray()
    @IsDefined()
    @ValidateNested()
    @Expose()
    @Type(() => UserAutomaticEmail)
    public automaticEmails: UserAutomaticEmail[] = [];

    @CDColumn()
    @Expose()
    @IsBoolean()
    public suspiciousCCActivity: boolean = false;

    @CDColumn()
    @Expose()
    @IsArray()
    @ValidateNested()
    @Type(() => SuspiciousCCActivityIssue)
    public suspiciousCCActivityIssues?: SuspiciousCCActivityIssue[];

    @CDColumn()
    @Expose()
    public version: number = 0;

    @CDColumn()
    @Expose()
    @IsDate()
    @TransformToDate
    public lastLoginAt?: Date;

    @GField(() => DistanceUnit)
    @CDColumn()
    @IsInt()
    @IsDefined()
    @Expose()
    public distanceUnit: DistanceUnit = DistanceUnit.Kilometers;

    @CDColumn()
    @IsString()
    @Expose()
    public discountCode?: string;

    @CDColumn()
    @IsOptional()
    @IsArray()
    @Expose()
    public fcmRegistrationTokens?: string[] = [];

    // set default based on order
    @GField(() => Currency)
    @CDColumn()
    @IsNumber()
    @IsDefined()
    @Expose()
    public currency: Currency = Currency.Euro;

    @GAuthorized()
    @GField()
    @CDColumn()
    @Expose()
    @IsDate()
    @IsOptional()
    @TransformToDate
    public verifiedAt?: Date;

    @GAuthorized()
    @GField(() => Wishlist)
    @Expose()
    @CDColumn()
    public wishlist: { routeIds: string[]; stopIds: string[]; cityIds: string[] } = {
        routeIds: [],
        stopIds: [],
        cityIds: [],
    };

    @GAuthorized()
    @GField()
    @CDColumn()
    @Expose()
    @IsOptional()
    public customerNote?: string;

    @GAuthorized()
    @GField({ nullable: true })
    @CDColumn()
    @Expose()
    @IsOptional()
    public stripeCustomerId?: string;

    /**
     This property is fully defined and used in the new API.
     It must be defined here as Column since it needs to be preserved in the database
     when manipulating with the User object in legacy.
     */
    @CDColumn()
    publicOffersPreferences?: any;

    /**
     This property is fully defined and used in the new API.
     It must be defined here as Column since it needs to be preserved in the database
     when manipulating with the User object in legacy.
     */
    @CDColumn()
    claimingToolConfig?: any;

    @CDColumn()
    @IsString()
    @Expose()
    cooperationAgreementSignedAs: DynamicFormConfigEntityType;

    @GAuthorized()
    @GField()
    @CDColumn()
    @IsBoolean()
    @Expose()
    optedOutOfMarketing: boolean = true;

    public isMangopayInformationSet(): this is { mangopayInformation: MangopayInformation } {
        return !!this.mangopayInformation && !!this.mangopayInformation[this.mangopayInformation.length - 1];
    }

    public get activeMangopayInformation(): MangopayInformation | undefined {
        if (this.driver || this.driversCompany) {
            const billingInformation = this.billingInformation.find((bi) => bi.isActive);

            if (billingInformation) {
                return this.mangopayInformation.find((mi) => mi.billingInformationId === billingInformation._id);
            }
        }

        return this.mangopayInformation.find((mi) => isUndefinedOrNull(mi.disabledAt));
    }

    public get activeMangopayInformationIndex(): number {
        if (this.driver || this.driversCompany) {
            const billingInformation = this.billingInformation.find((bi) => bi.isActive);

            if (billingInformation) {
                return this.mangopayInformation.findIndex((mi) => mi.billingInformationId === billingInformation._id);
            }
        }

        return this.mangopayInformation.findIndex((mi) => isUndefinedOrNull(mi.disabledAt));
    }

    public getActiveMangopayInformationOrFail() {
        const info = this.activeMangopayInformation;
        if (!info) {
            throw Error(`No active mangopay information found on user ${this._id}`);
        }
        return info;
    }

    public get invoicingBillingInformation(): BillingInformation | undefined {
        return this.billingInformation.find((bi) => bi.isInvoiceInformation && !bi.deletedAt);
    }

    public get fullName(): string {
        return [this.firstName, this.lastName].join(" ");
    }

    public isKYCValid(): boolean {
        try {
            if (!this.isMangopayInformationSet()) {
                return false;
            }
            const active = this.getActiveMangopayInformationOrFail();
            return active.KYCLevel === KYCLevel.Regular;
        } catch (e: any) {
            return false;
        }
    }
}

export function isDriver<TUser extends User>(user: TUser): user is DriverUser<TUser> {
    return !!user.driver;
}

export function isCompanyDriver<TUser extends User>(user: TUser): user is CompanyDriverUser<TUser> {
    return !!user.driver?.driversCompanyUserId;
}

export function isDriversCompany<TUser extends User>(user: TUser): user is DriversCompanyUser<TUser> {
    return !!user.driversCompany;
}
